import { Box, Typography } from '@mui/material';
import type React from 'react';
import { useThemeColors } from '../hooks/useThemeColors';
import MikiwameLogo from './MikiwameLogo';

interface Props {
  /** 何を待っているかを一言で（内部用語は使わない） */
  label: string;
  /** フクロウの大きさ(px) */
  size?: number;
  /** 画面全体の中央に置く（認証チェックなど、他に何も出せないとき） */
  fullHeight?: boolean;
  /** ロゴも出す。ヘッダーがまだ描かれていない起動時だけ true にする */
  brand?: boolean;
}

/**
 * 読み込み待ちの表示。
 *
 * ロゴのフクロウが、ふわりと上下しながら羽ばたき、ときどき首をかしげ、
 * 最後にフクロウらしく首をぐるりと一周させる。動きはCSSのみ（index.css の mk-* ）で、
 * 画像も外部ライブラリも使わないので認証チェック中でもすぐ出せる。
 *
 * 絵は切り絵（紙を3枚重ねた厚み）。翼・胴・頭をそれぞれ層にして影を落とし、
 * 層ごとにわずかに遅れて浮かせている。動きの値は従来のままで、変えたのは厚みだけ。
 */
export default function MikiwameLoader({ label, size = 128, fullHeight, brand }: Props) {
  const t = useThemeColors();
  // 白目と瞳は胴体のティールの上に乗るので、ライト/ダークとも同じ色で成立する。
  // トークンの色を使うとダークで反転して見えてしまう。
  const sclera = '#fdf6e8';
  const pupil = '#141826';

  return (
    <Box
      className="mk-loader"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2.5,
        ...(fullHeight
          ? { minHeight: 'var(--app-vh)', width: '100%' }
          : { flex: 1, minHeight: 0, py: 6 }),
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 128 128"
        aria-hidden
        sx={{ width: size, height: size, overflow: 'visible' }}
      >
        <title>読み込み中</title>
        <defs>
          {/* 切り絵の厚み。紙が台から浮いている分の影 */}
          <filter id="mk-paper" x="-30%" y="-30%" width="160%" height="160%">
            <feDropShadow
              dx="0"
              dy="2.5"
              stdDeviation="2"
              floodColor="#0b1220"
              floodOpacity="0.28"
            />
          </filter>
        </defs>
        <ellipse
          className="mk-shadow"
          cx="64"
          cy="116"
          rx="26"
          ry="4"
          fill={t.ink}
          opacity="0.14"
        />
        <g className="mk-owl" filter="url(#mk-paper)">
          <g className="mk-layer">
            <g className="mk-wing mk-wing-l">
              <path
                d="M38 66 C26 72 26 92 38 100 C42 92 42 74 38 66 Z"
                fill={t.teal}
                opacity="0.6"
              />
            </g>
            <g className="mk-wing mk-wing-r">
              <path
                d="M90 66 C102 72 102 92 90 100 C86 92 86 74 90 66 Z"
                fill={t.teal}
                opacity="0.6"
              />
            </g>
          </g>
          <g className="mk-layer mk-layer-2">
            <ellipse cx="64" cy="80" rx="31" ry="30" fill={t.teal} />
            {/* 胴の腹。上の紙がめくれて下の紙が見えているように薄く置く */}
            <path
              d="M49 88 C49 80 55.7 73 64 73 C72.3 73 79 80 79 88 C79 96 72.3 105 64 105 C55.7 105 49 97 49 88 Z"
              fill={sclera}
              opacity="0.62"
            />
          </g>
          <g className="mk-layer mk-layer-3">
            <g className="mk-head">
              <path d="M42 36 L47 17 L58 32 Z" fill={t.teal} />
              <path d="M86 36 L81 17 L70 32 Z" fill={t.teal} />
              <circle cx="64" cy="54" r="30" fill={t.teal} />
              <g className="mk-eye">
                <circle cx="51" cy="52" r="13" fill={sclera} />
                <circle className="mk-pupil" cx="51" cy="52" r="6" fill={pupil} />
              </g>
              <g className="mk-eye" style={{ '--mk-delay': '0.05s' } as React.CSSProperties}>
                <circle cx="77" cy="52" r="13" fill={sclera} />
                <circle className="mk-pupil" cx="77" cy="52" r="6" fill={pupil} />
              </g>
              <path d="M64 62 l6 11 -12 0 Z" fill={t.indigo} />
            </g>
          </g>
        </g>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: t.ink }}>
        {brand && <MikiwameLogo width={Math.round(size * 1.0)} />}
        <Typography
          sx={{
            fontSize: 12.5,
            fontWeight: 700,
            letterSpacing: '.18em',
            pl: '.18em',
            color: t.muted,
            mt: brand ? 1.25 : 0,
          }}
        >
          {label}
        </Typography>
        <Box
          className="mk-bar"
          sx={{ mt: 2, width: Math.round(size * 0.95) }}
          style={{ '--mk-bar-bg': t.line, '--mk-bar-fg': t.teal } as React.CSSProperties}
        />
      </Box>
    </Box>
  );
}
